import * as React from "react";
import * as Intl from "react-intl";
import classnames from "classnames";
import Button from "msem-lib/es/components/button";
import IntegerPicker from "msem-lib/es/components/integer-picker";
import SelectField from "msem-lib/es/components/select-field";
import css from "./pax-plan-form.module.css";

const Ages = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];

const MAX_ADULTS = 20;
const MAX_CHILDREN = 9;

interface PaxPlanFormProps {
  onValidate: (pax: { adults: number; children: number; agesChildren: number[] }) => () => void;
  adults?: number;
  children?: number;
  agesChildren?: number[];
  compact?: boolean;
  minPax?: number;
  maxPax?: number;
}

const PaxPlanForm = ({
  onValidate,
  adults,
  children,
  agesChildren,
  compact = false,
  minPax,
  maxPax,
}: PaxPlanFormProps) => {
  const intl = Intl.useIntl();
  const [selectedAdults, setAdults] = React.useState(adults || 0);
  const [selectedChildren, setChildren] = React.useState(children || 0);
  const [selectedChildrenAges, setChildrenAges] = React.useState(agesChildren || []);

  const totalPax = selectedAdults + selectedChildren;
  // Sans preset paxMax, les deux compteurs restent indépendants (comportement historique).
  // Avec paxMax, le total est plafonné : le max de chaque compteur correspond au reste du budget.
  const adultsMax = maxPax === undefined ? MAX_ADULTS : Math.max(0, Math.min(MAX_ADULTS, maxPax - selectedChildren));
  const childrenMax =
    maxPax === undefined ? MAX_CHILDREN : Math.max(0, Math.min(MAX_CHILDREN, maxPax - selectedAdults));
  // La valeur courante peut déjà violer les bornes (pax plan repris du sessionStorage, ou preset
  // resserré depuis la dernière visite) : on ne la corrige pas d'office, on bloque la validation.
  const isBelowMin = minPax !== undefined && totalPax < minPax;
  const isAboveMax = maxPax !== undefined && totalPax > maxPax;
  const isMaxReached = maxPax !== undefined && totalPax >= maxPax;
  // Bornes confondues : le nombre de personnes est imposé, pas encadré. On l'annonce d'un seul
  // tenant plutôt que d'alterner entre un message de plancher et une alerte de plafond atteint.
  const isExactPax = minPax !== undefined && minPax === maxPax;

  const changeAdults = (val: number) => {
    setAdults(val);
  };

  const changeChildren = (val: number) => {
    const oldVal = selectedChildren;
    const ages =
      val > oldVal ? [...selectedChildrenAges, 12] : selectedChildrenAges.slice(0, selectedChildrenAges.length - 1);
    setChildren(val);
    setChildrenAges(ages);
  };

  const changeAgeChildren = (index: number) => (val: string | number) => {
    const value = parseInt(val as string);
    const ages = [...selectedChildrenAges.slice(0, index), value, ...selectedChildrenAges.slice(index + 1)];
    setChildrenAges(ages);
  };

  return (
    <>
      <div className={classnames(css.paxPlan, { [css.compact]: compact })}>
        <div className={css.content}>
          <div className={css.adults}>
            <IntegerPicker min={0} max={adultsMax} value={selectedAdults} onChange={changeAdults} />
            <div className={css.label}>{intl.formatMessage({ id: "paxPlanFormAdults" })}</div>
          </div>
          <div>
            <div className={css.children}>
              <IntegerPicker min={0} max={childrenMax} value={selectedChildren} onChange={changeChildren} />
              <div className={css.label}>{intl.formatMessage({ id: "paxPlanFormChildren" })}</div>
            </div>
            {selectedChildren > 0 ? (
              <div className={css.childrenSuffix}>{intl.formatMessage({ id: "paxPlanFormChildrenSuffix" })}</div>
            ) : null}
            {selectedChildrenAges.map((age: number, index: number) => {
              return (
                <div key={index} className={css.ageRow}>
                  <div className={css.ageRowLabel}>
                    {intl.formatMessage({ id: "paxPlanFormChildrenCount" }, { count: index + 1 })}
                  </div>
                  <div className={css.select}>
                    <SelectField
                      value={age}
                      onChange={changeAgeChildren(index)}
                      label={""}
                      noLabelSpaceReservation
                      noErrorSpaceReservation
                      inline={false}
                    >
                      {Ages.map((option, idx) => (
                        <option key={idx} value={option}>
                          {intl.formatMessage({ id: "paxPlanFormChildrenAge" }, { count: option })}
                        </option>
                      ))}
                    </SelectField>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {isExactPax && (
        <div className={classnames(css.boundWarning, { [css.boundInfo]: !isBelowMin && !isAboveMax })}>
          {intl.formatMessage({ id: "paxPlanFormExactWarning" }, { count: minPax })}
        </div>
      )}
      {!isExactPax && isBelowMin && (
        <div className={css.boundWarning}>{intl.formatMessage({ id: "paxPlanFormMinWarning" }, { min: minPax })}</div>
      )}
      {!isExactPax && isMaxReached && (
        <div className={css.boundWarning}>{intl.formatMessage({ id: "paxPlanFormMaxWarning" }, { max: maxPax })}</div>
      )}
      <Button
        tag="a"
        onClick={onValidate({ adults: selectedAdults, children: selectedChildren, agesChildren: selectedChildrenAges })}
        contained
        disabled={isBelowMin || isAboveMax}
      >
        {intl.formatMessage({ id: "validate" })}
      </Button>
    </>
  );
};

export default PaxPlanForm;
